// ============================================================================
// Office hours — full-week fill + closed-day detection
// ----------------------------------------------------------------------------
// Property `office_hours` rows are often a partial week (weekdays only) or
// include Saturday/Sunday with empty / "Closed" times. Every hours surface
// should still list those days as "Closed" rather than dropping them.
// ============================================================================

export interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export const DAY_LABELS: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

export const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

const CLOSED_WORDS = new Set(['closed', 'n/a', 'na', 'none', '-']);

export function normalizeDay(day: string | null | undefined): string {
  return (day || '').trim().toLowerCase().slice(0, 3);
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function isClosedWord(val: string | null | undefined): boolean {
  return hasValue(val) && CLOSED_WORDS.has(val.trim().toLowerCase());
}

/**
 * A day is closed when it is missing, both times are empty, or either time
 * is an explicit closed marker ("Closed", "N/A", …).
 */
export function isClosedHours(
  openTime?: string | null,
  closeTime?: string | null,
): boolean {
  if (isClosedWord(openTime) || isClosedWord(closeTime)) return true;
  return !hasValue(openTime) && !hasValue(closeTime);
}

/**
 * Expands a partial office-hours list into the full week in canonical order
 * (Mon–Sun). Any day of the week missing from the collection is included with
 * no open/close time so it renders as "Closed". Non-standard day labels in the
 * data are appended after the canonical week. Canonical days always use the
 * full weekday name so grouped ranges read "Monday – Friday", not "MON – FRI".
 */
export function fillWeek(hours: OfficeHourEntry[]): OfficeHourEntry[] {
  const byDay = new Map<string, OfficeHourEntry>();
  for (const h of hours) {
    const key = normalizeDay(h.day);
    if (key && !byDay.has(key)) byDay.set(key, h);
  }
  const extraKeys = [...byDay.keys()].filter((k) => !DAY_ORDER.includes(k as (typeof DAY_ORDER)[number]));
  return [...DAY_ORDER, ...extraKeys].map((key) => {
    const entry = byDay.get(key);
    return {
      day: DAY_LABELS[key] || entry?.day || key,
      open_time: entry?.open_time ?? null,
      close_time: entry?.close_time ?? null,
    };
  });
}
